import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Input, Row, Select, Space, Table } from 'antd';
import PageWithFilters from 'components/page/page-with-filters';
import { PageWithFiltersContentContainer } from 'components/page/page.styles';
import { format } from 'date-fns';
import { Dayjs } from 'dayjs';
import useGetExporterFiles from 'hooks/data/useGetExporterFiles';
import React, { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import useGetExportedFile from 'hooks/useGetExportedFile';
import useToastContext from 'hooks/useToastContext';

const { RangePicker } = DatePicker;

enum ProcessTypes {
  CLEARING = 'CLEARING',
  SICORE = 'SICORE',
  INVOICE = 'INVOICE',
  MOVEMENTS = 'MOVEMENTS',
  CASHBACK = 'CASHBACK',
  IPM = 'IPM',
  CARD_LOAD = 'CARD_LOAD',
  CARDHOLDER_LOAD = 'CARDHOLDER_LOAD',
  SIRCAR = 'SIRCAR',
  BALANCE = 'BALANCE'
}

type SearchExporterFilesFilters = {
  fileName: string;
  processType: string;
  dateRanges: [Dayjs, Dayjs];
};

const ExporterFilters = ({ onSubmit }: { onSubmit: (data: SearchExporterFilesFilters) => void }) => {
  const [form] = Form.useForm();
  const [expand, setExpand] = useState(false);

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
  };

  const onFinish = (values: SearchExporterFilesFilters) => {
    onSubmit(values);
  };

  const onReset = () => {
    onSubmit({});
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      style={formStyle}
      onFinish={onFinish}
      onReset={onReset}
      initialValues={{ processType: ProcessTypes.CLEARING }}
    >
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item<SearchExporterFilesFilters> name="fileName" label="Nombre">
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<SearchExporterFilesFilters> name="processType" label="Proceso">
            <Select options={Object.values(ProcessTypes).map((pName) => ({ label: pName, value: pName }))} />
          </Form.Item>
        </Col>
        {!expand && (
          <Col span={8}>
            <div style={{ textAlign: 'right' }}>
              <Space size="small">
                <Button type="primary" htmlType="submit">
                  Buscar
                </Button>
                <Button htmlType="reset">Limpiar</Button>
                <a
                  style={{ fontSize: 12 }}
                  onClick={() => {
                    setExpand(!expand);
                  }}
                >
                  <DownOutlined rotate={expand ? 180 : 0} /> Más
                </a>
              </Space>
            </div>
          </Col>
        )}
        {expand && (
          <>
            <Col span={8}>
              <Form.Item<SearchExporterFilesFilters> name="dateRanges" label="Fechas">
                <RangePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}></Col>
            <Col span={8}></Col>
            <Col span={8} style={{ marginBottom: '1.5rem' }}>
              <div style={{ textAlign: 'right' }}>
                <Space size="small">
                  <Button type="primary" htmlType="submit">
                    Buscar
                  </Button>
                  <Button htmlType="reset">Limpiar</Button>
                  <a
                    style={{ fontSize: 12 }}
                    onClick={() => {
                      setExpand(!expand);
                    }}
                  >
                    <DownOutlined rotate={expand ? 180 : 0} /> Menos
                  </a>
                </Space>
              </div>
            </Col>
          </>
        )}
      </Row>
    </Form>
  );
};

const Exporter = () => {
  const toast = useToastContext();
  const [filters, setFilters] = useState({
    processType: ProcessTypes.CLEARING,
    fileName: '',
    startDate: '',
    endDate: '',
  });
  const { files, isLoading } = useGetExporterFiles(
    filters.processType,
    filters.fileName,
    filters.startDate,
    filters.endDate
  );
  const { execute } = useGetExportedFile();

  const handleGetFile = async (name: string) => {
    try {
      const response = await execute({
        params: { name: name, process: filters.processType },
      });
      if(response?.status === 200 || response?.status == 204) {
        window.open(response?.data?.url);
        toast.success("Exito", "Archivo descargado");
      } else {
        toast.error("Error", "Ocurrió un error al descargar el archivo");
      }
    } catch (ex: any) {
      toast.error("Error", "Ocurrió un error al descargar el archivo");
    }
  };

  const handleSearchClick = (data: SearchExporterFilesFilters) => {
    setFilters({
      processType: ProcessTypes[data.processType],
      fileName: data.fileName,
      startDate: data.dateRanges?.[0] ? `${format(data.dateRanges?.[0]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
      endDate: data.dateRanges?.[1] ? `${format(data.dateRanges?.[1]?.toDate(), 'yyyy-MM-dd')}T23:59:00` : '',
    });
  };

  const columns: ColumnsType<{ id: string }> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Nombre de Archivo',
    },
    {
      dataIndex: 'processType',
      key: 'processType',
      title: 'Tipo de archivo',
      render: () => filters.processType || ProcessTypes.CLEARING,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => {
        return (
          <Space size="middle">
            <Button onClick={() => {
              handleGetFile(record?.id);
            }}
              size="small" type="link" icon={<DownloadOutlined />}>
              Descargar
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <PageWithFilters title="Exportador">
      <PageWithFiltersContentContainer>
        <ExporterFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <Table
          dataSource={files}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: files?.length,
            defaultPageSize: 10,
            defaultCurrent: 1,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true,
          }}
        />
      </PageWithFiltersContentContainer>
    </PageWithFilters>
  );
};

export default Exporter;
